/**
 * Per-campaign "Additional links & attachments" (owner request, 2026-08-31).
 *
 * Beyond the single CTA link, a campaign can carry up to 5 extra resources —
 * links the AI is allowed to send when the moment fits: the company website,
 * a meeting link, or a hosted file (a deck, a case study). Each row pairs a
 * URL with a short label and, crucially, a description telling the AI WHEN
 * to send it ("send when the lead is interested so they can sign up").
 *
 * Backend contract: `campaigns.settings.resources` = optional array of
 * `{ url, label, description }`, max 5, http(s) URLs, label <= 60 chars,
 * description <= 300 chars. `cta_type` + `offer_link` remain the primary CTA.
 *
 * Shared by the create-campaign wizard (Step 1) and the Edit Campaign
 * dialog so the two surfaces cannot drift. The row shape is editing state
 * (blank rows tolerated while typing); `cleanResourceRows` produces the
 * payload array and `resourceRowsValid` gates Next/Save.
 */
import { Check, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { CampaignResource } from './types'

export const MAX_RESOURCES = 5
export const RESOURCE_LABEL_MAX = 60
export const RESOURCE_DESC_MAX = 300

const URL_PATTERN = /^https?:\/\/\S+\.\S+/

/** Editing shape for one row — all fields may be blank mid-typing. */
export type ResourceRow = {
  readonly url: string
  readonly label: string
  readonly description: string
}

export const EMPTY_RESOURCE_ROW: ResourceRow = { url: '', label: '', description: '' }

function rowIsEmpty(row: ResourceRow): boolean {
  return !row.url.trim() && !row.label.trim() && !row.description.trim()
}

/**
 * Validation message for one row, or null when the row is fine. Fully-empty
 * rows are fine (they're pruned from the payload); a partially-filled row
 * must have a valid http(s) URL and a "when to send" description.
 */
export function resourceRowError(row: ResourceRow): string | null {
  if (rowIsEmpty(row)) return null
  const url = row.url.trim()
  if (!url) return 'Add the link URL, or clear this row.'
  if (!URL_PATTERN.test(url)) return 'Enter a full URL starting with https://'
  if (!row.description.trim()) return 'Tell the AI when to send this link.'
  return null
}

/** Whether every row is either empty or complete — gates Next/Save. */
export function resourceRowsValid(rows: readonly ResourceRow[]): boolean {
  return rows.every((row) => resourceRowError(row) === null)
}

/** Prune empty rows and trim/cap fields into the API payload shape. */
export function cleanResourceRows(rows: readonly ResourceRow[]): CampaignResource[] {
  return rows
    .filter((row) => !rowIsEmpty(row))
    .map((row) => ({
      url: row.url.trim(),
      label: row.label.trim().slice(0, RESOURCE_LABEL_MAX),
      description: row.description.trim().slice(0, RESOURCE_DESC_MAX),
    }))
    .slice(0, MAX_RESOURCES)
}

/** Stored campaign resources -> editing rows (for the Edit dialog prefill). */
export function resourcesToRows(
  resources: readonly CampaignResource[] | null | undefined,
): readonly ResourceRow[] {
  if (!resources) return []
  return resources.slice(0, MAX_RESOURCES).map((r) => ({
    url: r.url ?? '',
    label: r.label ?? '',
    description: r.description ?? '',
  }))
}

/**
 * Per-row placeholder examples (mirroring the owner's own examples) so the
 * first rows teach the pattern: what link, and when the AI should send it.
 */
const ROW_PLACEHOLDERS: readonly ResourceRow[] = [
  {
    url: 'https://yourcompany.com',
    label: 'e.g. Website',
    description: 'e.g. Send when the lead is interested so they can sign up.',
  },
  {
    url: 'https://calendly.com/you/intro-call',
    label: 'e.g. Meeting link',
    description: 'e.g. Send in the final follow-up so they can book at their convenience.',
  },
  {
    url: 'https://yourcompany.com/case-study.pdf',
    label: 'e.g. Case study',
    description: 'e.g. Send when the lead asks for proof or examples.',
  },
]

type SettingsLinkSuggestionProps = {
  /** The link saved in Settings, Offer (resourceLink); empty/undefined hides. */
  readonly settingsLink: string | undefined
  /** The campaign's current offer link — suggestion only shows while empty. */
  readonly currentLink: string
  readonly onUse: (link: string) => void
}

/**
 * One-click prefill from Settings: when the campaign has no link yet but the
 * user saved one under Settings, Offer, offer it right under the input
 * ("or paste a different link" stays the normal path).
 */
export function SettingsLinkSuggestion({
  settingsLink,
  currentLink,
  onUse,
}: SettingsLinkSuggestionProps) {
  const suggested = settingsLink?.trim() ?? ''
  if (!suggested || !URL_PATTERN.test(suggested)) return null
  if (currentLink.trim() !== '') return null

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-dashed bg-muted/20 px-2.5 py-1.5">
      <p className="min-w-0 truncate text-xs text-muted-foreground">
        Use the link from your Settings:{' '}
        <span className="font-medium text-secondary-foreground">{suggested}</span>
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 shrink-0 px-2 text-xs"
        onClick={() => onUse(suggested)}
      >
        <Check className="h-3 w-3" />
        Use it
      </Button>
    </div>
  )
}

type ResourcesEditorProps = {
  readonly rows: readonly ResourceRow[]
  readonly onChange: (rows: readonly ResourceRow[]) => void
  /** Prefix for input ids so two mounted editors don't collide. */
  readonly idPrefix: string
}

/**
 * The "Additional links & attachments" section: a list of URL + label +
 * "when should the AI send this?" rows, capped at MAX_RESOURCES.
 */
export function ResourcesEditor({ rows, onChange, idPrefix }: ResourcesEditorProps) {
  function updateRow(index: number, patch: Partial<ResourceRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index))
  }

  function addRow() {
    if (rows.length >= MAX_RESOURCES) return
    onChange([...rows, EMPTY_RESOURCE_ROW])
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <Label>
          Additional links &amp; attachments{' '}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <p className="text-xs text-muted-foreground">
          Extra links the AI can send when the moment fits: your website, a
          meeting link, or an attachment. Attachments are links to hosted
          files, like a deck or a case study. For each one, tell the AI when
          to send it.
        </p>
      </div>

      {rows.map((row, index) => {
        const placeholders = ROW_PLACEHOLDERS[Math.min(index, ROW_PLACEHOLDERS.length - 1)]!
        const error = resourceRowError(row)
        return (
          <div key={index} className="flex flex-col gap-2 rounded-md border bg-muted/10 p-2.5">
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2">
                  <Input
                    id={`${idPrefix}-resource-url-${index}`}
                    type="url"
                    aria-label={`Link ${index + 1} URL`}
                    placeholder={placeholders.url}
                    value={row.url}
                    onChange={(e) => updateRow(index, { url: e.target.value })}
                    className="h-8 text-xs"
                  />
                  <Input
                    id={`${idPrefix}-resource-label-${index}`}
                    aria-label={`Link ${index + 1} label`}
                    placeholder={placeholders.label}
                    maxLength={RESOURCE_LABEL_MAX}
                    value={row.label}
                    onChange={(e) => updateRow(index, { label: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label
                    htmlFor={`${idPrefix}-resource-desc-${index}`}
                    className="text-[11px] font-normal text-muted-foreground"
                  >
                    When should the AI send this?
                  </Label>
                  <textarea
                    id={`${idPrefix}-resource-desc-${index}`}
                    className="min-h-[44px] max-h-[120px] resize-y rounded-md border border-input bg-background px-2 py-1.5 text-xs placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={placeholders.description}
                    maxLength={RESOURCE_DESC_MAX}
                    value={row.description}
                    onChange={(e) => updateRow(index, { description: e.target.value })}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeRow(index)}
                aria-label={`Remove link ${index + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        )
      })}

      {rows.length < MAX_RESOURCES ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 self-start px-2.5 text-xs"
          onClick={addRow}
        >
          <Plus className="h-3 w-3" />
          Add link or attachment
          <span className="text-muted-foreground">
            ({rows.length}/{MAX_RESOURCES})
          </span>
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          Maximum of {MAX_RESOURCES} links reached.
        </p>
      )}
    </div>
  )
}
